// CrmContactsFrame — AA "HubSpot | Contacts" report page parity
// (client-analytics contacts/multi-pipeline expansion, GAP 1).
//
// KPI row (total contacts + up to four standout lifecycle-stage tiles,
// matching AA's Leads/Customers/Subscribers/Opportunities cards) above a
// full by_lifecycle bar chart, then a by_country bar chart (GAP 1 fix,
// 2026-08-09 — sourced from a raw read_client_data_source contacts pull,
// see the backend CrmContactsSection docstring for the live-verification).
// `by_country` still renders adaptively — only when the backend actually
// has rows for it (e.g. a non-HubSpot CRM, or before the raw contacts
// backfill has run for a given client) — rather than shipping a
// permanently-empty chart.
//
// Self-contained: recharts + named theme tokens only, same bar-chart
// convention as LocalSurfaceFrame.tsx.

import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatLocalDateRange } from "@/components/client-analytics/primitives";
import type { CrmContactsSection } from "@/types/clientAnalytics";

export interface CrmContactsFrameProps {
  contacts: CrmContactsSection;
}

// Sample data for standalone preview / storybook-style rendering. Mirrors
// the real ALIBI Music shape (2026-08-09).
export const demoCrmContacts: CrmContactsSection = {
  total: 14603,
  by_lifecycle: [
    { label: "Lead", value: 9317 },
    { label: "Salesqualifiedlead", value: 2795 },
    { label: "Marketingqualifiedlead", value: 2277 },
    { label: "Customer", value: 105 },
    { label: "Opportunity", value: 57 },
    { label: "Subscriber", value: 47 },
  ],
  by_country: [
    { label: "United States", value: 331 },
    { label: "United Kingdom", value: 45 },
    { label: "India", value: 15 },
    { label: "Canada", value: 12 },
  ],
  by_country_sample_size: 500,
  period_start: "2026-05-01",
  period_end: "2026-08-08",
  change_pct: -2.24,
};

const ROW_HEIGHT = 32;
const MIN_CHART_HEIGHT = 160;

// Confirmed live on ALIBI Music: `by_country` came back with 40+ distinct
// countries, most of them 1-2 contacts against a 331-contact "United
// States" bar -- a chart ~1,280px+ tall (40 * ROW_HEIGHT) of near-invisible
// bars, with ~1,900px of scroll before the rest of the page. Capped to the
// top 8 by count (comfortably covers every country an SMB client's contact
// list realistically has meaningful volume in) plus one synthetic remainder
// row aggregating the rest -- never silently drops data, just stops
// rendering a bar too short to read.
const BY_COUNTRY_DISPLAY_CAP = 8;

// The four lifecycle labels AA's own "HubSpot | Contacts" report page pulls
// out into standalone KPI tiles (Leads/Customers/Subscribers/Opportunities).
// HubSpot's raw lifecycle_stage values are lowercase/concatenated
// ("Salesqualifiedlead", "Marketingqualifiedlead") so matching is
// case-insensitive and substring-based, not an exact-label lookup.
const HIGHLIGHT_STAGES: { key: string; label: string; match: (label: string) => boolean }[] = [
  { key: "leads", label: "Leads", match: (l) => l.toLowerCase() === "lead" },
  { key: "customers", label: "Customers", match: (l) => l.toLowerCase() === "customer" },
  { key: "subscribers", label: "Subscribers", match: (l) => l.toLowerCase() === "subscriber" },
  {
    key: "opportunities",
    label: "Opportunities",
    match: (l) => l.toLowerCase().includes("opportunity") || l.toLowerCase().includes("qualifiedlead"),
  },
];

function formatPctDelta(pct: number | null | undefined): string | null {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return null;
  const sign = pct >= 0 ? "▲" : "▼";
  return `${sign} ${Math.abs(pct).toFixed(2)}%`;
}

// COMPARISON LABELING (2026-08-09): mirrors DealPipelinePanel's
// ComparisonDelta/ComparisonBadges — a bare "▼2.24%" doesn't say what it's
// measured against. The backend's `change_pct` here is the same
// preceding-equal-length-window mechanism as CrmPeriodComparison, but
// CrmContactsSection doesn't (yet) carry the prior window's exact dates the
// way CrmPeriodComparison.prior_period_start/end does, so the badge shows
// the basis ("Prior period") without a specific range until that's added.
// A year-ago basis slots in as a second entry once the backend ships it;
// until then it's always omitted (undefined pct), never a fabricated or
// mislabeled figure.
interface ComparisonDelta {
  basisLabel: string;
  pct: number | null | undefined;
  rangeLabel?: string | null;
}

// BADGE/HEADER COLLISION FIX (2026-08-10, confirmed live on ALIBI's contacts
// panel: "PRIOR PERIODLEADS" rendered with no gap). Root cause: the pct text
// and the basis label ("PRIOR PERIOD") used to share ONE `whitespace-nowrap`
// span, and the KpiTile that hosts this badge sets an explicit
// `min-w-[110px]`. An explicit min-width (vs. the default `auto`) removes
// the flexbox spec's automatic min-content protection for that tile, so
// when several KpiTiles share a row (Total Contacts + up to 4 highlight
// stages), the tile can be flex-shrunk narrower than its own nowrap badge
// text needs -- and with `overflow: visible` (the default, still true
// here), that text doesn't get clipped, it paints straight past the tile's
// right edge and over whatever sits at the start of the NEXT tile ("LEADS"),
// with no gap because nothing actually reserved room for it.
// Two independent fixes, belt-and-suspenders:
//   1. The basis label now sits on its OWN line (`block`) under the pct
//      value instead of trailing it on the same nowrap line -- the single
//      line the badge needs is now only as wide as the WIDER of the two
//      texts, not their sum, which is usually enough on its own to fit
//      inside a 110px tile.
//   2. KpiTile below gets `overflow-hidden` as a hard backstop: if a basis
//      label is ever long enough to still not fit, it now clips inside its
//      own tile's box instead of bleeding into a neighbor -- a truncated
//      label read alone is far less confusing than two unrelated labels
//      fused together with no gap.
function ComparisonBadges({ deltas }: { deltas: ComparisonDelta[] }) {
  const visible = deltas.filter(
    (d): d is ComparisonDelta & { pct: number } =>
      d.pct !== null && d.pct !== undefined && Number.isFinite(d.pct),
  );
  if (visible.length === 0) return null;
  return (
    <div className="flex flex-col items-end gap-0.5">
      {visible.map((d) => {
        const text = formatPctDelta(d.pct);
        if (!text) return null;
        const tone = d.pct >= 0 ? "text-[hsl(var(--status-success))]" : "text-[hsl(var(--status-error))]";
        return (
          <span
            key={d.basisLabel}
            className="flex flex-col items-end"
            title={d.rangeLabel ? `${d.basisLabel} vs ${d.rangeLabel}` : d.basisLabel}
          >
            <span className={`whitespace-nowrap text-dash-context font-semibold tabular ${tone}`}>{text}</span>
            <span className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {d.basisLabel}
            </span>
          </span>
        );
      })}
    </div>
  );
}

// Parsing goes through the shared primitives.parseLocalDate — see its comment
// for why `new Date("YYYY-MM-DD")` rendered a day early west of UTC.
export function formatDateRange(start?: string | null, end?: string | null): string | null {
  return formatLocalDateRange(start, end);
}

function KpiTile({
  label,
  value,
  deltas,
  dateRange,
}: {
  label: string;
  value: number;
  deltas?: ComparisonDelta[];
  dateRange?: string | null;
}) {
  return (
    <div className="flex-1 min-w-[110px] overflow-hidden flex flex-col gap-1 p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {deltas && <ComparisonBadges deltas={deltas} />}
      </div>
      {dateRange && <span className="text-dash-context text-muted-foreground">{dateRange}</span>}
      <span className="text-dash-value font-bold tabular text-foreground">{value.toLocaleString()}</span>
    </div>
  );
}

export function CrmContactsFrame({ contacts }: CrmContactsFrameProps) {
  const byLifecycle = contacts.by_lifecycle ?? [];
  const byCountry = contacts.by_country ?? [];
  const dateRange = formatDateRange(contacts.period_start, contacts.period_end);

  const highlights = HIGHLIGHT_STAGES.map((h) => {
    const row = byLifecycle.find((r) => h.match(r.label));
    return row ? { key: h.key, label: h.label, value: row.value } : null;
  }).filter((h): h is { key: string; label: string; value: number } => h !== null);

  const sortedLifecycle = [...byLifecycle].sort((a, b) => b.value - a.value);
  const chartHeight = Math.max(MIN_CHART_HEIGHT, sortedLifecycle.length * ROW_HEIGHT);

  // Cap `by_country` to the top N by count + one remainder row -- see
  // BY_COUNTRY_DISPLAY_CAP's doc comment. Sorted once here (rather than
  // inline at the chart below) so the cap and the sort agree on the same
  // ordering.
  const sortedCountries = [...byCountry].sort((a, b) => b.value - a.value);
  const countriesOmitted = Math.max(0, sortedCountries.length - BY_COUNTRY_DISPLAY_CAP);
  const displayedCountries =
    countriesOmitted > 0
      ? [
          ...sortedCountries.slice(0, BY_COUNTRY_DISPLAY_CAP),
          {
            label: `Other (${countriesOmitted} more)`,
            value: sortedCountries.slice(BY_COUNTRY_DISPLAY_CAP).reduce((sum, c) => sum + c.value, 0),
          },
        ]
      : sortedCountries;
  const countryChartHeight = Math.max(MIN_CHART_HEIGHT, displayedCountries.length * ROW_HEIGHT);

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-border bg-card">
        <div className="flex divide-x divide-border flex-wrap">
          <KpiTile
            label="Total Contacts"
            value={contacts.total}
            dateRange={dateRange}
            deltas={[
              // No prior_period_start/end on CrmContactsSection yet (see
              // ComparisonDelta doc comment) — basis shown, no hover range.
              { basisLabel: "Prior period", pct: contacts.change_pct, rangeLabel: null },
              { basisLabel: "YoY", pct: undefined, rangeLabel: undefined },
            ]}
          />
          {highlights.map((h) => (
            <KpiTile key={h.key} label={h.label} value={h.value} />
          ))}
        </div>
      </div>

      {sortedLifecycle.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
            Contacts by lifecycle stage
          </div>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={sortedLifecycle} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 4 }}>
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }} />
              <YAxis
                type="category"
                dataKey="label"
                width={140}
                tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid var(--color-border)",
                  fontSize: 12,
                }}
                formatter={(value: number) => [value.toLocaleString(), "Contacts"]}
              />
              <Bar dataKey="value" fill="var(--color-coral)" radius={[0, 4, 4, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Adaptive: only renders once the backend actually has a country
          dimension to show — see module docstring. */}
      {byCountry.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              Contacts by country
            </div>
            <div className="flex items-center gap-2">
              {countriesOmitted > 0 && (
                <span className="text-dash-context text-muted-foreground">
                  Showing {BY_COUNTRY_DISPLAY_CAP} of {sortedCountries.length}
                </span>
              )}
              {contacts.by_country_sample_size != null && contacts.by_country_sample_size < contacts.total && (
                <span className="text-dash-context text-muted-foreground">
                  Showing {contacts.by_country_sample_size.toLocaleString()} of {contacts.total.toLocaleString()}
                </span>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={countryChartHeight}>
            <BarChart
              data={displayedCountries}
              layout="vertical"
              margin={{ top: 4, right: 16, bottom: 0, left: 4 }}
            >
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }} />
              <YAxis
                type="category"
                dataKey="label"
                width={110}
                tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid var(--color-border)",
                  fontSize: 12,
                }}
                formatter={(value: number) => [value.toLocaleString(), "Contacts"]}
              />
              <Bar dataKey="value" fill="var(--color-teal)" radius={[0, 4, 4, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
